import React, { useEffect } from "react";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import { Label } from "@/modern/components/ui/label";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { duplicateBoardSchema, DuplicateBoardFormData } from "@/react/schema/duplicateBoard.schema";
import { duplicateBoardAndTestDrive } from "@/network/network";
import { MessageDialog } from "@bryntum/schedulerpro";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { shouldShowAsyncDialog } from "@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup";

const DUPLICATE_OPTIONS = [
  { key: "all", label: "All", description: "This is a great option for experimenting and training." },
  { key: "template", label: "Template", description: "Includes all status, category, tags, teams, type, and phase settings; icons; and saved views. This is the best option for creating a board template." },
  { key: "tasks", label: "Tasks", description: "Includes task general data, files, notes, budgets, checklists, and linked contacts." },
  { key: "resources", label: "Resources", description: "Includes resource general data, files, notes, checklists, and linked contacts." },
  { key: "clients", label: "Clients", description: "Includes client general data, files, notes, checklists, and linked contacts." },
  { key: "users", label: "Users", description: "Includes all board users, roles, permissions, and linked contacts." },
  { key: "contacts", label: "Contacts", description: "Includes all board contacts." }
] as const;

interface DuplicateBoardEditorContext {
  boardStore?: any;
  boardManager?: any;
  onComplete?: () => void;
  editorHandler?: any;
}

interface DuplicateBoardEditorProps {
  record: {
    record: {
      id: number;
      name?: string;
      display_name?: string;
      [key: string]: any;
    };
    context: DuplicateBoardEditorContext;
  };
  onClose: () => void;
  editorInstance?: any;
}

export const DuplicateBoardEditor = ({ record: { record, context }, onClose, editorInstance }: DuplicateBoardEditorProps) => {
  const boardName = record?.name || record?.display_name || "Board";

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid }
  } = useForm<DuplicateBoardFormData>({
    resolver: zodResolver(duplicateBoardSchema),
    mode: "onChange",
    defaultValues: {
      boardName: "",
      all: false,
      template: false,
      tasks: false,
      resources: false,
      clients: false,
      users: false,
      contacts: false
    }
  });

  const watchAll = watch("all");
  const watchTemplate = watch("template");
  const watchTasks = watch("tasks");
  const watchResources = watch("resources");
  const watchClients = watch("clients");
  const watchUsers = watch("users");
  const watchContacts = watch("contacts");
  const watchBoardName = watch("boardName");

  // Handle "All" checkbox logic
  useEffect(() => {
    if (watchAll) {
      setValue("template", true);
      setValue("tasks", true);
      setValue("resources", true);
      setValue("clients", true);
      setValue("users", true);
      setValue("contacts", true);
    }
  }, [watchAll, setValue]);

  // Check if all individual options are selected to update "All"
  useEffect(() => {
    const allIndividualOptionsSelected =
      watchTemplate &&
      watchTasks &&
      watchResources &&
      watchClients &&
      watchUsers &&
      watchContacts;

    if (allIndividualOptionsSelected && !watchAll) {
      setValue("all", true);
    } else if (!allIndividualOptionsSelected && watchAll) {
      setValue("all", false);
    }
  }, [watchTemplate, watchTasks, watchResources, watchClients, watchUsers, watchContacts, watchAll, setValue]);

  const onSubmit = (data: DuplicateBoardFormData) => {
    const boardDataCopyOptions = data.all
      ? ['everything']
      : Object.entries(data)
          .filter(([key, value]) => key !== 'boardName' && key !== 'all' && value === true)
          .map(([key]) => key);

    if (!boardDataCopyOptions.length) {
      MessageDialog.alert({
        title: "Duplicate Options Required",
        message: "At least one option must be selected when duplicating a board."
      });
      return;
    }

    // Use reusable dialog utility
    const showDialog = shouldShowAsyncDialog('BOARD_DUPLICATION');
    showDialog && context?.editorHandler?.openEditor(
      EDITOR_KEYS.asyncTaskMessageEditor,
      null,
      { record: "BOARD_DUPLICATION" },
      { useExisting: true, by: "recordType" }
    );

    duplicateBoardAndTestDrive(record.id, data.boardName, boardDataCopyOptions)
      .then(() => {
        context?.onComplete?.();
        onClose();
      })
      .catch((error) => {
        MessageDialog.alert({
          title: "Duplication Failed",
          message: error?.response?.data?.detail || "Failed to duplicate board. Please try again."
        });
      });
  };

  const isSaveDisabled = !watchBoardName.trim() || !isValid;

  const footerRenderer = () => (
    <div className="border-t border-border bg-card w-full">
      <div className="flex items-center justify-end gap-2 p-3">
        <Button
          onClick={onClose}
          variant="outline"
          className="text-blue-600 dark:text-blue-400"
        >
          <i className="fa-light fa-close mr-2" />
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="outline"
          className="text-green-600 dark:text-green-400"
          disabled={isSaveDisabled}
        >
          <i className="fa-sharp fa-thin fa-clapperboard mr-2" />
          Duplicate
        </Button>
      </div>
    </div>
  );

  return (
    <BaseModal
      record={record}
      onClose={onClose}
      title={`Duplicate ${boardName || "Board"}`}
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "50em",
        height: "42em",
        minWidth: "40em",
        minHeight: "30em",
        maxWidth: "100vw",
        maxHeight: "90vh"
      }}
      modal={true}
      version={undefined}
      swivuAssistanceConfig={undefined}
      headerIconCls=""
      footerRenderer={footerRenderer}
      footerClassName="!p-0"
    >
      <BaseModalBody className="!p-4 flex flex-col h-full">
        <div className="flex flex-col gap-3">
          {DUPLICATE_OPTIONS.map((option, index) => (
            <div key={option.key}>
              {index === 1 && <hr className="my-1 border-border" />}
              <div className="flex items-start gap-3 py-1">
                <Checkbox
                  id={option.key}
                  checked={watch(option.key as keyof DuplicateBoardFormData) as boolean}
                  onCheckedChange={(checked) => {
                    setValue(option.key as keyof DuplicateBoardFormData, checked as boolean);
                  }}
                  className="mt-0.5"
                />
                <div className="flex flex-col gap-0.5 flex-1">
                  <Label htmlFor={option.key} className="font-semibold cursor-pointer leading-tight">
                    {option.label}
                  </Label>
                  <p className="text-sm text-muted-foreground leading-snug">
                    {option.description}
                  </p>
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-col gap-2 mt-3 mb-2">
            <Label htmlFor="boardName">Board Name</Label>
            <Input
              id="boardName"
              {...register("boardName")}
              placeholder={`Duplicating ${boardName}. Please enter a new name.`}
              className="w-full"
            />
            {errors.boardName && (
              <p className="text-sm text-destructive">{errors.boardName.message}</p>
            )}
          </div>
        </div>
      </BaseModalBody>
    </BaseModal>
  );
};
